import { Suspense, lazy } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";

import Calculator from "./components/Calculator";
import Checkout from "./components/Checkout";
import GoogleAnalyticsCallback from "./components/GoogleAnalytics/GoogleAnalyticsCallback";
import GoogleAnalyticsDashboard from "./components/GoogleAnalytics/GoogleAnalyticsDashboard";
import Layout from "./components/Layout";
import OTAEngine from "./components/OTAEngine";
import OTAReport from "./components/OTAReport";
import PerceptionEngine from "./components/PerceptionEngine";
import PerceptionVisibilityReport from "./components/PerceptionVisibilityReport";
import PersonaEngine from "./components/PersonaEngine";
import PersonaVisibilityReport from "./components/PersonaVisibilityReport";
import Pricing from "./components/Pricing";
import Report from "./components/Report";
import ScheduledReports from "./components/ScheduledReports";
import Subscription from "./components/Subscription";
import Trends from "./components/Trends";
import VisibilityEngine from "./components/VisibilityEngine";
import VisibilityReport from "./components/VisibilityReport";
import { AiReputationEngine } from "./components/ai-reputation/AiReputationEngine";
import { AiReputationReportPage } from "./components/ai-reputation/AiReputationReportPage";
import HomePage from "./components/home/HomePage";
import LandingPage from "./components/landing/LandingPage";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import TermsAndConditions from "./components/legal/TermsAndConditions";
import PartnersPage from "./components/partners/PartnersPage";
import { AuthProvider } from "./contexts/AuthContext";

const AggregateHistoryPage = lazy(
  () => import("./components/aggregate-visibility/AggregateHistoryRoute"),
);
const CreateAggregateReportPage = lazy(
  () => import("./components/aggregate-visibility/CreateAggregateRoute"),
);
const AggregateReportPage = lazy(
  () => import("./components/aggregate-visibility/AggregateDetailRoute"),
);
const AssessmentPage = lazy(() => import("./components/ai-readiness/AssessmentPage"));
const ReadinessReportPage = lazy(() => import("./components/ai-readiness/ReportPage"));
const aggregateRoute = (Component) => (
  <Suspense
    fallback={
      <section className="p-8" role="status">
        Loading aggregate reports…
      </section>
    }
  >
    <Component />
  </Suspense>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/audit" element={<LandingPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/scheduled-reports" element={<ScheduledReports />} />
            <Route path="/trends" element={<Trends />} />
            <Route path="/audit/report/:audit_id" element={<Report />} />
            <Route path="/ai-reputation" element={<AiReputationEngine />} />
            <Route path="/ai-reputation/report/:report_id" element={<AiReputationReportPage />} />
            <Route
              path="/ai-readiness"
              element={
                <Suspense
                  fallback={
                    <p className="p-8" role="status">
                      Loading assessment…
                    </p>
                  }
                >
                  <AssessmentPage />
                </Suspense>
              }
            />
            <Route
              path="/ai-readiness/report/:assessment_id"
              element={
                <Suspense
                  fallback={
                    <p className="p-8" role="status">
                      Loading report…
                    </p>
                  }
                >
                  <ReadinessReportPage />
                </Suspense>
              }
            />
            <Route path="/llm-visibility" element={<VisibilityEngine />} />
            <Route
              path="/llm-visibility/aggregates"
              element={aggregateRoute(AggregateHistoryPage)}
            />
            <Route
              path="/llm-visibility/aggregates/new"
              element={aggregateRoute(CreateAggregateReportPage)}
            />
            <Route
              path="/llm-visibility/aggregates/:aggregate_report_id"
              element={aggregateRoute(AggregateReportPage)}
            />
            <Route
              path="/llm-visibility/report/:visibility_report_id"
              element={<VisibilityReport />}
            />
            <Route path="/ota-direct" element={<OTAEngine />} />
            <Route path="/ota-direct/report/:ota_report_id" element={<OTAReport />} />
            <Route path="/persona" element={<PersonaEngine />} />
            <Route path="/persona/trends" element={<PersonaVisibilityReport trendsOnly />} />
            <Route path="/persona/report/:report_id" element={<PersonaVisibilityReport />} />
            <Route path="/perception" element={<PerceptionEngine />} />
            <Route path="/perception/trends" element={<PerceptionVisibilityReport trendsOnly />} />
            <Route path="/perception/report/:report_id" element={<PerceptionVisibilityReport />} />
            <Route path="/google-analytics" element={<GoogleAnalyticsDashboard />} />
            {/* Path is dictated by the redirect URI registered on the Google OAuth client. */}
            <Route path="/api/auth/callback/google" element={<GoogleAnalyticsCallback />} />
          </Routes>
        </Layout>
      </AuthProvider>
    </Router>
  );
}

export default App;
